import { format } from "date-fns";
import { type FC, useMemo } from "react";
import { usePutPortfolioManualValueMutation } from "web_myplann/Api/Portfolio/Mutations/usePutPortfolioManualValueMutation";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type {
	CurrencyEnum,
	GoalPortfolioResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ValueDrawer } from "web_myplann/Portfolio/PortfolioShared/ValueDrawer";
import { FlatRowInput } from "web_myplann/Shared/Components/FlatRow/FlatRowInput";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { joinTruthy } from "web_myplann/Shared/Utils/textFormatters";

const PageResources = Resources.PortfolioDetail.Property.ValueDrawer;

type Props = {
	open: boolean;
	portfolio: GoalPortfolioResponseDTO;
	currencyCode?: CurrencyEnum;
	onClose: () => void;
};

export const GoalPortfolioValueDrawer: FC<Props> = ({
	open,
	portfolio,
	currencyCode,
	onClose,
}) => {
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const { mutate: saveValue, isPending } = usePutPortfolioManualValueMutation();

	const today = useMemo(() => format(new Date(), DateFormat.Display), []);
	const hasPreviousValue = portfolio.portfolioValue != null;

	const handleSave = (value: number, onSaved: () => void) => {
		saveValue(
			{
				portfolioId: portfolio.portfolioId,
				request: { value, date: format(new Date(), DateFormat.Iso) },
			},
			{ onSuccess: onSaved },
		);
	};

	return (
		<ValueDrawer
			open={open}
			onClose={onClose}
			title={t(
				hasPreviousValue ? PageResources.updateTitle : PageResources.fillTitle,
			)}
			valueLabel={t(PageResources.newValueLabel)}
			currencySign={t(Resources.Goals.Amount.currencySign)}
			subtitle={joinTruthy([portfolio.name, portfolio.producer])}
			isPending={isPending}
			onSave={handleSave}
			closeButton
			beforeInput={
				hasPreviousValue && (
					<FlatRowInput
						label={t(PageResources.previousValue)}
						value={formatAmount(portfolio.portfolioValue ?? 0, currencyCode)}
						readOnly
					/>
				)
			}
			afterInput={
				<FlatRowInput
					label={t(Resources.Common.ToDate)}
					value={today}
					readOnly
					isLast
				/>
			}
		/>
	);
};
